import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { useSelector } from "react-redux";
import {
  TextField,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import OutboxIcon from "@mui/icons-material/Outbox";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PersonLookup2 from "../../../components/PersonLookup2";
import { gridStyle } from "../../../utils/formStyles";
import {
  findContactList,
  findGeneralSearchProfiles,
  findSearchClearanceLevels,
  findSearchProfiles,
} from "../../../pages/Security/SecFunction";
import { getSpecificUserPermissions } from "../../../reducers/PermissionsReducer";

import PersonDialog from "../../PersonDialog";
import { mapDictionaryOptions } from "../../../utils/functions";
import { getService } from "../../../reducers/service";

const useStyles = makeStyles((theme) => ({
  item: gridStyle(200, 320),
  autoComplete: gridStyle(200, 300),
  disabledText: {
    color: "rgba(0, 0, 0, 0.38)",
  },
  checkedBox: {
    width: "100%",
  },
}));

function AddUser(props) {
  const classes = useStyles();
  const [username, setUsername] = useState("");
  const [person, setPerson] = useState(null);
  const [profile, setProfile] = useState(null);
  const [generalProfile, setGeneralProfile] = useState(null);
  const [clearance, setClearance] = useState(null);
  const [email, setEmail] = useState("");
  const [allProfiles, setAllProfiles] = useState([]);
  const [allGeneralProfiles, setAllGeneralProfiles] = useState([]);
  const [allClearanceLevels, setAllClearanceLevels] = useState([]);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [defaultAgency, setDefaultAgency] = useState("");
  const [NCICUsername, setNCICUsername] = useState("");
  const [ori, setOri] = useState(null);
  const [rid, setRid] = useState(null);
  const [forcePW, setForcePW] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [ApplicationName, setApplicationName] = useState(null);
  const [showAddPersonDialog, setShowAddPersonDialog] = useState();
  const [userNameError, setUserNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [ptsPersonID, setPtsPersonID] = useState(null);
  const [PhoneNumber, setPhoneNumber] = useState("");
  const { setNewUser, newUserData, selectedUser } = props;
  const [credentialAnchorEl, setCredentialAnchorEl] = React.useState(null);
  const openPopup = Boolean(credentialAnchorEl);
  const agencies = useSelector(
    (state) => state.dictionary.dictionary.ptsAgencies
  );
  const [personDialogSource, setPersonDialogSource] = useState(null);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const [numberList, setNumberList] = React.useState([]);
  const [emailList, setEmailList] = React.useState([]);
  const [NCICEmail, setNCICEmail] = useState("");
  const sexOptions = mapDictionaryOptions(dictionary.codeSex);
  const raceOptions = mapDictionaryOptions(dictionary.codeRace);
  const ethnicityOptions = mapDictionaryOptions(dictionary.codeEthnicity);
  const contactInfo = mapDictionaryOptions(
    dictionary.codeContactInfoMethodTypes
  );
  const suffixOptions = mapDictionaryOptions(dictionary.codeNameSuffixes);
  const [defaulAgencyPermissionError, setDefaultAgencyPermissionError] =
    useState("");

  useEffect(() => {
    fetchAMSProfiles();
    fetchProfiles();
    fetchClearanceLevels();
  }, []);

  useEffect(() => {
    if (props.selectedProfile) {
      handleChange(
        "ptsProfileLookupId",
        props.selectedProfile,
        setProfile,
        props.selectedProfile?.ptsProfileID || null
      );
    }
  }, [props.selectedProfile]);

  useEffect(() => {
    if (props.selectedProfile) {
      handleChange(
        "ptsGeneralProfileLookupId",
        props.selectedProfile,
        setGeneralProfile,
        props.selectedProfile?.ptsProfileID || null
      );
    }
  }, [props.selectedProfile]);

  useEffect(() => {
    if (person) {
      setNewUser((user) => {
        return { ...user, ptsPersonId: person.ptsPersonID };
      });
    } else {
      setNewUser((user) => {
        return { ...user, ptsPersonId: null };
      });
    }
  }, [person]);

  useEffect(() => {
    setAgencyPermissionError();
  }, [profile, defaultAgency]);

  const getPartyPerson = (ptsPersonID) => {
    const service = getService("cad");
    return service.get({
      type: "get-party-person",
      data: { ptsPersonID },
    });
  };

  useEffect(() => {
    if (allProfiles && allClearanceLevels && selectedUser) {
      const { ptsAMSProfileID, ptsClearanceLevelID, ptsProfileID } =
        selectedUser;
      const currentProfile = allProfiles.find(
        (profile) => profile.ptsProfileID === ptsAMSProfileID
      );
      const currentGeneralProfile = allGeneralProfiles.find(
        (profile) => profile.ptsProfileID === ptsProfileID
      );
      const currentClearance = allClearanceLevels.find(
        (c) => c.ptsClearanceLevelID === ptsClearanceLevelID
      );
      const currentAgency = (agencies || []).find(
        (agn) => agn.AgencyID === selectedUser.AgencyID
      );

      getPartyPerson(selectedUser.ptsPersonID).then((person) => {
        setSelectedPerson(person);
        setPerson(person);
        setNewUser((user) => {
          return { ...user, ptsPersonId: selectedUser.ptsPersonID };
        });
      });

      setProfile(currentProfile);
      setGeneralProfile(currentGeneralProfile);
      setClearance(currentClearance);
      setUsername(selectedUser.Username);
      setIsLockedOut(selectedUser.IsLockedOut);
      setIsDisabled(selectedUser.IsDisabled);
      setEmail(selectedUser.PasswordEmail);
      setDefaultAgency(currentAgency);
      setNCICUsername(selectedUser.NCICUsername);
      setApplicationName(selectedUser.ApplicationName);
      setPhoneNumber(selectedUser.PhoneNumber);
      setRid(selectedUser.RID);
      setOri(selectedUser.ORI);
      setNCICEmail(selectedUser.NCICEmail || "");
    }
  }, [allProfiles, allClearanceLevels]);

  useEffect(() => {
    if (person && person?.ptsPersonID) {
      fetchContactList(person?.ptsPersonID);
    } else {
      setEmailList([]);
      setNumberList([]);
    }
  }, [person && person?.ptsPersonID]);

  const fetchContactList = async (id) => {
    const list = await findContactList(id);
    if (list && list.length > 0) {
      const filterList = list.filter((contact) => contact.Info);
      const phones = filterList.filter(
        (contact) => contact.ContactMethod === "Phone"
      );
      const emails = filterList.filter(
        (contact) => contact.ContactMethod === "Email"
      );
      setNumberList(phones);
      setEmailList(emails);
    }
  };

  const handleShowPopupClick = (event) => {
    setCredentialAnchorEl(event.currentTarget);
  };
  const handleClosePopup = () => {
    setCredentialAnchorEl(null);
  };

  const handleChangeWithValidation = (
    key,
    value,
    setState,
    validateInput,
    setError
  ) => {
    setState(value);
    setNewUser((user) => {
      return { ...user, [key]: value };
    });
    if (validateInput(value)) setError(false);
    else setError(true);
  };

  const handleChange = (key, value, setState, stateValue) => {
    setState(value);
    setNewUser((user) => {
      return { ...user, [key]: stateValue };
    });
  };

  const validateUserName = (value) => {
    if (!/^[a-zA-Z0-9_@.-]+$/.test(value)) {
      return false;
    } else {
      return true;
    }
  };

  const validateEmail = (email) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const fetchAMSProfiles = async () => {
    const result = await findSearchProfiles();
    setAllProfiles(result);
  };

  const fetchProfiles = async () => {
    const result = await findGeneralSearchProfiles();
    setAllGeneralProfiles(result);
  };

  const fetchClearanceLevels = async () => {
    const result = await findSearchClearanceLevels();
    setAllClearanceLevels(result);
  };

  const setAgencyPermissionError = async () => {
    if (profile && defaultAgency) {
      const userPermissions = await getSpecificUserPermissions(
        profile.ptsProfileID
      );
      const filteredPermissions = Array.isArray(userPermissions)
        ? userPermissions.filter(
            (perm) =>
              perm?.AgencyID === defaultAgency?.AgencyID ||
              perm?.AgencyID == null
          )
        : [];

      const hasPermission =
        filteredPermissions.length > 0 &&
        filteredPermissions.some((perm) => perm?.Permissions === 1);

      if (!hasPermission) {
        setDefaultAgencyPermissionError(
          "Assigned profile does not include any permissions for default agency."
        );
        return;
      }
    }
    setDefaultAgencyPermissionError("");
  };

  const handleSendUserName = (medium) => {
    props.saveUser({ type: "username", medium });
    handleClosePopup();
  };
  const renderUsername = () => {
    return (
      <Box
        style={{
          marginLeft: 10,
          display: "flex",
          justifyContent: "left",
          gap: "4px",
        }}
      >
        <TextField
          label="Username*"
          variant="outlined"
          value={username}
          disabled={selectedUser?.Username}
          size="small"
          onChange={(e) =>
            handleChangeWithValidation(
              "userName",
              e.target.value,
              setUsername,
              validateUserName,
              setUserNameError
            )
          }
          fullWidth
          className={classes.item}
          error={userNameError}
          style={{ flex: selectedUser?.Username ? 95 : 100 }}
          helperText={userNameError ? "Provide a valid Username." : ""}
        />
        {selectedUser?.Username ? (
          <Tooltip title="Send Username">
            <IconButton
              style={{ flex: 5, marginTop: "-10px" }}
              onClick={handleShowPopupClick}
            >
              <OutboxIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        ) : null}
        <Menu
          id="basic-menu"
          anchorEl={credentialAnchorEl}
          open={openPopup}
          onClose={handleClosePopup}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          {newUserData.email ? (
            <MenuItem onClick={() => handleSendUserName("EMAIL")}>
              Email
            </MenuItem>
          ) : (
            <div
              style={{ padding: "10px", textAlign: "center", width: "100%" }}
            >
              <Tooltip title="Please Add a Email Address">
                <span className={classes.disabledText}>Email</span>
              </Tooltip>
            </div>
          )}

          {newUserData.PhoneNumber ? (
            <MenuItem onClick={() => handleSendUserName("SMS")}>SMS</MenuItem>
          ) : (
            <div
              style={{ padding: "10px", textAlign: "center", width: "100%" }}
            >
              <Tooltip title="Please Add a Phone Number">
                <span className={classes.disabledText}>SMS</span>
              </Tooltip>
            </div>
          )}
        </Menu>
      </Box>
    );
  };

  const updateNumberList = (item) => {
    handleChange(
      "PhoneNumber",
      item.Info || "",
      setPhoneNumber,
      item.Info || ""
    );
    setNumberList([item, ...numberList]);
  };
  const updateEmailList = (item) => {
    handleChange("email", item.Info, setEmail, item.Info);
    setEmailList([item, ...emailList]);
  };
  const renderEmail = () => {
    return (
      <Box
        style={{
          marginLeft: 10,
          display: "flex",
          width: "321px",
        }}
      >
        <Autocomplete
          disabled={props.disabled}
          fullWidth
          autoHighlight
          options={emailList || []}
          value={{ Info: email }}
          getOptionLabel={(option) => option?.Info || ""}
          size="small"
          onChange={(event, value) => {
            const newEmail = value?.Info || "";
            setEmail(newEmail);
            handleChange("email", newEmail, setEmail, newEmail);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.Info}>
              <b>{option.MethodType || ""}</b> - {option.Info || ""}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Email"
              size="small"
              variant="outlined"
              fullWidth
              InputProps={{
                ...params.InputProps,
                autoComplete: "new-password",
                startAdornment: (
                  <>
                    <IconButton
                      disabled={!person?.ptsPersonID}
                      onClick={() => {
                        setPersonDialogSource("email");
                        setShowAddPersonDialog(true);
                      }}
                    >
                      <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    );
  };

  const renderPhoneNumber = () => {
    return (
      <Box sx={{ ml: 1.5, width: "321px", height:"10px" }}>
        <Autocomplete
          disabled={props.disabled}
          className={props.className}
          fullWidth
          autoHighlight
          options={numberList || []}
          value={numberList.find((n) => n.Info === PhoneNumber) || null}
          getOptionLabel={(option) => option.Info || ""}
          size="small"
          onChange={(event, value) => {
            const newNumber = value?.Info || "";
            handleChange("PhoneNumber", newNumber, setPhoneNumber, newNumber);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.Info}>
              <b>{option.MethodType || ""}</b> - {option.Info || ""}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Phone Number"
              size="small"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                autoComplete: "new-password",
                startAdornment: (
                  <>
                    <IconButton
                      disabled={!person?.ptsPersonID}
                      onClick={() => {
                        setPersonDialogSource("phone");
                        setShowAddPersonDialog(true);
                      }}
                    >
                      <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    );
  };

  const renderPersonLookup = () => {
    return (
      <Box style={{ marginLeft: 10 }}>
        <PersonLookup2
          className={classes.item}
          onPersonChange={setPerson}
          label="Person Lookup*"
          addEnabled={true}
          selectedPerson={selectedPerson}
          ptsPersonID={ptsPersonID}
          setShowAddPersonDialog={setShowAddPersonDialog}
          setPersonDialogSource={setPersonDialogSource}
          fullWidth
        />
      </Box>
    );
  };
  const renderAMSProfileLookup = () => {
    return (
      <Box sx={{ ml: 1 }}>
        <Autocomplete
          disablePortal
          options={allProfiles || []}
          className={classes.item}
          renderInput={(params) => (
            <TextField
              {...params}
              label="AMS Profile Lookup*"
              variant="outlined"
            />
          )}
          onChange={(e, value) => {
            setProfile(value); // store whole object
            setNewUser((user) => ({
              ...user,
              ptsProfileLookupId: value?.ptsProfileID || null,
            }));
          }}
          getOptionLabel={(option) => option.Label || ""}
          renderOption={(props, option) => (
            <li {...props}>
              {option.Label} {option.Type ? `- ${option.Type}` : ""}
            </li>
          )}
          isOptionEqualToValue={(option, value) =>
            option.ptsProfileID === value?.ptsProfileID
          }
          size="small"
          value={profile || null} // keep object directly
        />
      </Box>
    );
  };

  const renderProfileLookup = () => {
    return (
      <Box style={{ marginLeft: 10 }}>
        <Autocomplete
          disablePortal
          options={allGeneralProfiles || []}
          className={classes.item}
          renderInput={(params) => (
            <TextField {...params} label="Profile Lookup*" variant="outlined" />
          )}
          onChange={(e, value) =>
            handleChange(
              "ptsGeneralProfileLookupId",
              value,
              setGeneralProfile,
              value?.ptsProfileID || null
            )
          }
          getOptionLabel={(option) => option.Label}
          renderOption={(props, option) => (
            <li {...props}>
              {option.Label} {option.Type ? `- ${option.Type}` : ""}
            </li>
          )}
          getOptionSelected={(option, value) =>
            option.ptsProfileID === value?.ptsProfileID
          }
          size="small"
          value={generalProfile}
        />
      </Box>
    );
  };

  const renderClearanceLevel = () => {
    return (
      <Box sx={{ ml: 1 }}>
        <Autocomplete
          disablePortal
          options={allClearanceLevels || []}
          className={classes.item}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Clearance Levels Lookup"
              variant="outlined"
            />
          )}
          onChange={(e, value) =>
            handleChange(
              "ptsClearanceLevelId",
              value,
              setClearance,
              value?.ptsClearanceLevelID || null
            )
          }
          getOptionLabel={(option) => option?.Level || ""}
          renderOption={(props, option) => (
            <li {...props}>
              {option.Level}{" "}
              {option.Description ? `- ${option.Description}` : ""}
            </li>
          )}
          size="small"
          value={clearance}
        />
      </Box>
    );
  };

  const renderLockedOut = () => {
    return (
      <Box style={{ marginLeft: 10 }}>
        <FormControlLabel
          className={classes.checkedBox}
          control={
            <Checkbox
              checked={isLockedOut}
              onChange={() =>
                handleChange(
                  "isLockedOut",
                  !isLockedOut,
                  setIsLockedOut,
                  !isLockedOut
                )
              }
            />
          }
          label="Locked Out"
        />
      </Box>
    );
  };

  const renderDisabled = () => {
    return (
      <Box style={{ marginLeft: 20 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isDisabled}
              onChange={() =>
                handleChange(
                  "isDisable",
                  !isDisabled,
                  setIsDisabled,
                  !isDisabled
                )
              }
            />
          }
          label="Disable"
        />
      </Box>
    );
  };

  const renderForcePW = () => {
    return (
      <Box style={{ marginLeft: 20 }}>
        <FormControlLabel
          className={classes.checkedBox}
          control={
            <Checkbox
              checked={forcePW}
              onChange={() =>
                handleChange("forcePWChange", !forcePW, setForcePW, !forcePW)
              }
            />
          }
          label="Force Password Change"
        />
      </Box>
    );
  };

  const renderDefaultAgency = () => {
    return (
      <Box sx={{ ml: 1 }}>
        <Autocomplete
          disablePortal
          options={agencies || []}
          className={classes.item}
          onChange={(e, value) =>
            handleChange(
              "defaultAgencyId",
              value,
              setDefaultAgency,
              value?.AgencyID || null
            )
          }
          getOptionLabel={(option) =>
            option?.AgencyID ? String(option.AgencyID) : ""
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Default Agency"
              variant="outlined"
              error={Boolean(defaulAgencyPermissionError)}
              helperText={defaulAgencyPermissionError}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              {option.AgencyID}{" "}
              {option.Description ? `- ${option.Description}` : ""}
            </li>
          )}
          size="small"
          fullWidth
          value={defaultAgency}
        />
      </Box>
    );
  };

  const renderNCICUsername = () => {
    return (
      <Box style={{ marginLeft: 10 }}>
        <TextField
          label="NCIC Username"
          variant="outlined"
          placeholder="max length 30"
          inputProps={{ maxLength: 30 }}
          value={NCICUsername}
          size="small"
          onChange={(e) =>
            handleChange(
              "NCICUserName",
              e.target.value,
              setNCICUsername,
              e.target.value
            )
          }
          fullWidth
          className={classes.item}
        />
      </Box>
    );
  };

  const renderNCICEmail = () => {
    return (
      <Box style={{ marginLeft: 10 }}>
        <TextField
          label="NCIC Email"
          variant="outlined"
          value={NCICEmail}
          fullWidth
          size="small"
          className={classes.item}
          type="email"
          error={emailError}
          helperText={emailError ? "Please enter a valid email address" : ""}
          onChange={(e) =>
            handleChangeWithValidation(
              "NCICEmail",
              e.target.value,
              setNCICEmail,
              validateEmail,
              setEmailError
            )
          }
        />
      </Box>
    );
  };
  const renderORI = () => {
    return (
      <Box style={{ marginLeft: 10 }}>
        <TextField
          label="ORI"
          variant="outlined"
          value={ori}
          size="small"
          onChange={(e) =>
            handleChange("ori", e.target.value, setOri, e.target.value)
          }
          fullWidth
          className={classes.item}
        />
      </Box>
    );
  };

  const renderRID = () => {
    return (
      <Box style={{ marginLeft: 10 }}>
        <TextField
          label="RID"
          variant="outlined"
          value={rid}
          size="small"
          onChange={(e) =>
            handleChange("rid", e.target.value, setRid, e.target.value)
          }
          fullWidth
          className={classes.item}
        />
      </Box>
    );
  };

  const renderApplication = () => {
    const applicationList = [
      { name: "RMS", index: 1 },
      { name: "CAD", index: 2 },
      { name: "CAD-Mobile", index: 3 },
      { name: "AMS", index: 4 },
    ];

    return (
      <Box style={{ marginLeft: 10 }}>
        <Autocomplete
          style={{ padding: 5, paddingBottom: 20 }}
          options={applicationList || []}
          disablePortal
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Default Application"
              variant="outlined"
            />
          )}
          renderOption={(props, option) => <li {...props}>{option.name}</li>}
          size="small"
          value={
            applicationList.find((opt) => opt.name === ApplicationName) || null
          }
          onChange={(e, value) => {
            setApplicationName(value?.name || "");
            handleChange(
              "ApplicationName",
              value?.name || "",
              setApplicationName,
              value?.name || null
            );
          }}
        />
      </Box>
    );
  };

  return (
    <div>
      {showAddPersonDialog && (
        <PersonDialog
          open={showAddPersonDialog}
          onClose={() => setShowAddPersonDialog(false)}
          genderOptions={sexOptions || []}
          raceOptions={raceOptions || []}
          ethnicityOptions={ethnicityOptions || []}
          contactInfoOptions={contactInfo || []}
          suffixOptions={suffixOptions || []}
          createRecordFor={null}
          source={personDialogSource}
          ptsPersonID={person?.ptsPersonID}
          updateNumberList={updateNumberList}
          updateEmailList={updateEmailList}
          setPtsPersonID={setPtsPersonID}
        />
      )}
      <Grid container item xs={12} style={{ marginTop: 10 }}>
        <Grid item xs={12} style={{ width: "100%" }}>
          {renderApplication()}
        </Grid>

        <Grid item xs={6} style={{ width: "100%" }}>
          {renderUsername()}
        </Grid>
        <Grid item xs={6} style={{ width: "100%" }}>
          {renderAMSProfileLookup()}
        </Grid>
      </Grid>

      <Grid container item xs={12} style={{ marginTop: 10 }}>
        <Grid item xs={6} style={{ width: "100%" }}>
          {renderPersonLookup()}
        </Grid>
        <Grid item xs={6} style={{ width: "100%" }}>
          {renderProfileLookup()}
        </Grid>
      </Grid>

      <Grid container item xs={12} style={{ marginTop: 10 }}>
        <Grid item xs={6} style={{ width: "100%" }}>
          {renderEmail()}
        </Grid>
        <Grid item xs={6} style={{ width: "100%" }}>
          {renderPhoneNumber()}
        </Grid>
      </Grid>
      <Grid container item xs={12} style={{ marginTop: 10 }}>
        <Grid item xs={6} style={{ width: "100%" }}>
          {renderClearanceLevel()}
        </Grid>
        <Grid item xs={6} style={{ width: "100%" }}>
          {renderDefaultAgency()}
        </Grid>
      </Grid>

      <Grid container item xs={12} style={{ marginTop: 10 }}>
        <Grid item xs={6} style={{ width: "100%" }}>
          {renderNCICUsername()}
        </Grid>
        <Grid item xs={6} style={{ width: "100%" }}>
          {renderNCICEmail()}
        </Grid>
      </Grid>
      <Grid container item xs={12} style={{ marginTop: 10 }}>
        <Grid item xs={6} style={{ width: "100%" }}>
          {renderRID()}
        </Grid>
        <Grid item xs={6} style={{ width: "100%" }}>
          {renderORI()}
        </Grid>
      </Grid>

      <Grid container item xs={12} style={{ marginTop: 10 }}>
        <Grid item xs={4} style={{ width: "100%" }}>
          {renderLockedOut()}
        </Grid>
        <Grid item xs={3} style={{ width: "100%" }}>
          {renderDisabled()}
        </Grid>
        <Grid item xs={5} style={{ width: "100%" }}>
          {renderForcePW()}
        </Grid>
      </Grid>
    </div>
  );
}

export default AddUser;
